import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Download, Loader2 } from "lucide-react";

import { PageHeader } from "@/components/shared/PageHeader";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDisplayDate } from "@/lib/dateFormat";
import { serverMessage } from "@/lib/apiError";
import {
  approveAccountantInviteManualReview,
  declineAccountantInviteManualReview,
  fetchAccountantInviteProfessionalCertificate,
  getAccountantInviteManualReviewDetail,
} from "@/services/accountantsService";

const ACCOUNTANT_INVITES_QUERY_KEY = ["platform", "accountant-invites"] as const;

function detailQueryKey(inviteId: string): readonly string[] {
  return ["platform", "accountant-manual-review-detail", inviteId];
}

export function AccountantPendingReviewDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const { inviteId } = useParams<{ inviteId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [certificateObjectUrl, setCertificateObjectUrl] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: detailQueryKey(inviteId ?? ""),
    queryFn: () => getAccountantInviteManualReviewDetail(inviteId!),
    enabled: Boolean(inviteId),
  });

  const hasCertificate = Boolean(data?.professionalCertificateUrl?.trim());

  const certificateBlobQuery = useQuery({
    queryKey: [...detailQueryKey(inviteId ?? ""), "professional-certificate"],
    queryFn: () => fetchAccountantInviteProfessionalCertificate(inviteId!),
    enabled: Boolean(inviteId) && hasCertificate,
  });

  useEffect(() => {
    const blob = certificateBlobQuery.data;
    if (!blob) {
      setCertificateObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setCertificateObjectUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [certificateBlobQuery.data]);

  useEffect(() => {
    if (certificateBlobQuery.isError) {
      toast.error(t("accountantManualReview.certificateLoadError"));
    }
  }, [certificateBlobQuery.isError, t]);

  const approveMutation = useMutation({
    mutationFn: () => approveAccountantInviteManualReview(inviteId!),
    onSuccess: async () => {
      toast.success(t("accountantManualReview.acceptSuccess"));
      await queryClient.invalidateQueries({ queryKey: ACCOUNTANT_INVITES_QUERY_KEY });
      void navigate("/accountants", { replace: true });
    },
    onError: (err) => {
      // SURFACE THE SERVER'S REASON. Approval runs
      // assertFiscalDeclarationComplete before it writes anything, so the
      // common refusal is "el profesional todavía no completó la declaración
      // fiscal" — precise and actionable. Discarding the error and printing a
      // generic "no se pudo actualizar" left an admin staring at a button that
      // would never work, with nothing saying the applicant simply has not
      // declared a RUT yet.
      toast.error(serverMessage(err) ?? t("accountantManualReview.actionError"));
    },
  });

  const declineMutation = useMutation({
    mutationFn: () =>
      declineAccountantInviteManualReview(inviteId!, {
        reason: declineReason.trim() || undefined,
      }),
    onSuccess: async () => {
      toast.success(t("accountantManualReview.declineSuccess"));
      setDeclineReason("");
      setDeclineOpen(false);
      await queryClient.invalidateQueries({ queryKey: ACCOUNTANT_INVITES_QUERY_KEY });
      void navigate("/accountants", { replace: true });
    },
    onError: (err) => {
      // SURFACE THE SERVER'S REASON. Approval runs
      // assertFiscalDeclarationComplete before it writes anything, so the
      // common refusal is "el profesional todavía no completó la declaración
      // fiscal" — precise and actionable. Discarding the error and printing a
      // generic "no se pudo actualizar" left an admin staring at a button that
      // would never work, with nothing saying the applicant simply has not
      // declared a RUT yet.
      toast.error(serverMessage(err) ?? t("accountantManualReview.actionError"));
    },
  });

  if (!inviteId) {
    return (
      <div className="space-y-4 p-4 md:p-8">
        <PageHeader
          title={t("accountantManualReview.detailTitle")}
          onBack={() => navigate("/accountants")}
        />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-4 md:p-8">
        <LoadingLogo size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4 p-4 md:p-8">
        <PageHeader
          title={t("accountantManualReview.detailTitle")}
          onBack={() => navigate("/accountants")}
        />
        <p className="text-sm text-destructive">{t("accountantManualReview.loadError")}</p>
      </div>
    );
  }

  if (data.manualApprovalStatus !== "pending") {
    return (
      <div className="space-y-4 p-4 md:p-8">
        <PageHeader
          title={t("accountantManualReview.detailTitle")}
          onBack={() => navigate("/accountants")}
        />
        <p className="text-sm text-muted-foreground">{t("accountantManualReview.notPending")}</p>
      </div>
    );
  }

  const showImagePreview =
    certificateBlobQuery.data != null && certificateBlobQuery.data.type.startsWith("image/");

  return (
    <div className="space-y-4 p-4 md:p-8">
      <PageHeader
        title={t("accountantManualReview.detailTitle")}
        description={t("accountantManualReview.detailSubtitle")}
        onBack={() => navigate("/accountants")}
      />

      <Card>
        <CardContent className="space-y-3 p-5 sm:p-6">
          <h2 className="text-sm font-semibold text-foreground">
            {t("accountantManualReview.sectionApplicant")}
          </h2>
          <dl className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">{t("auth.acceptAccountantInvitation.firstName")}</dt>
              <dd className="text-foreground">{data.validatedFirstName ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t("auth.acceptAccountantInvitation.secondFirstName")}
              </dt>
              <dd className="text-foreground">{data.validatedSecondFirstName ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t("auth.acceptAccountantInvitation.firstLastName")}</dt>
              <dd className="text-foreground">{data.validatedLastName ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">
                {t("auth.acceptAccountantInvitation.secondLastName")}
              </dt>
              <dd className="text-foreground">{data.validatedSecondLastName ?? "—"}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground">{t("auth.acceptAccountantInvitation.cedula")}</dt>
              <dd className="text-foreground">{data.validatedCedula ?? "—"}</dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-muted-foreground">{t("accountants.columnEmail")}</dt>
              <dd className="text-foreground">{data.email}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5 sm:p-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-sm font-semibold text-foreground">
              {t("accountantManualReview.sectionCertificate")}
            </h2>
            {hasCertificate ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 transition-colors duration-150"
                disabled={
                  certificateBlobQuery.isLoading ||
                  certificateBlobQuery.isError ||
                  !certificateObjectUrl
                }
                onClick={() => {
                  if (!certificateObjectUrl) return;
                  const anchor = document.createElement("a");
                  anchor.href = certificateObjectUrl;
                  anchor.target = "_blank";
                  anchor.rel = "noopener noreferrer";
                  anchor.download = "";
                  anchor.click();
                }}
              >
                <Download className="h-4 w-4" aria-hidden />
                {t("accountantManualReview.downloadCertificate")}
              </Button>
            ) : null}
          </div>
          {hasCertificate ? (
            certificateBlobQuery.isLoading ? (
              <div className="flex min-h-[min(70vh,560px)] items-center justify-center rounded-md border border-border bg-muted/20">
                <LoadingLogo size="lg" />
              </div>
            ) : certificateBlobQuery.isError ? (
              <p className="text-sm text-muted-foreground">
                {t("accountantManualReview.certificatePreviewUnavailable")}
              </p>
            ) : certificateObjectUrl ? (
              showImagePreview ? (
                <img
                  src={certificateObjectUrl}
                  alt=""
                  className="max-h-[min(70vh,560px)] w-full rounded-md border border-border object-contain bg-muted/30"
                />
              ) : (
                <iframe
                  title={t("accountantManualReview.sectionCertificate")}
                  src={certificateObjectUrl}
                  className="h-[min(70vh,560px)] w-full rounded-md border border-border bg-muted/30"
                />
              )
            ) : null
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
          {data.professionalCertificateUploadedAt ? (
            <p className="text-xs text-muted-foreground">
              {formatDisplayDate(data.professionalCertificateUploadedAt, { includeTime: true })}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-row flex-wrap items-center justify-between gap-4 pt-6">
          <Button
            type="button"
            variant="outline"
            className="transition-colors duration-150"
            disabled={approveMutation.isPending || declineMutation.isPending}
            onClick={() => setDeclineOpen(true)}
          >
            {t("accountantManualReview.decline")}
          </Button>
          <Button
            type="button"
            className="transition-colors duration-150"
            disabled={
              approveMutation.isPending ||
              declineMutation.isPending ||
              !data.professionalCertificateUrl?.trim()
            }
            onClick={() => approveMutation.mutate()}
          >
            {approveMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("accountantManualReview.accept")}
              </>
            ) : (
              t("accountantManualReview.accept")
            )}
          </Button>
        </CardContent>
      </Card>

      <Dialog
        open={declineOpen}
        onOpenChange={(open) => {
          if (!declineMutation.isPending) setDeclineOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("accountantManualReview.declineDialogTitle")}</DialogTitle>
            <DialogDescription>{t("accountantManualReview.declineDialogDescription")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="decline-reason">{t("accountantManualReview.declineReasonLabel")}</Label>
            <Textarea
              id="decline-reason"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder={t("accountantManualReview.declineReasonPlaceholder")}
              rows={3}
              className="resize-none"
              disabled={declineMutation.isPending}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              className="transition-colors duration-150"
              disabled={declineMutation.isPending}
              onClick={() => setDeclineOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="transition-colors duration-150"
              disabled={declineMutation.isPending}
              onClick={() => declineMutation.mutate()}
            >
              {declineMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("accountantManualReview.declineConfirm")}
                </>
              ) : (
                t("accountantManualReview.declineConfirm")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
